/**
 * 형제 문서(같은 점검 + 같은 양식) 조회 — 칩과 작성 화면이 **같은 목록**을 본다.
 *
 * 결정: `docs/decisions/2026-09-01-composer-split-generation.md` §④.
 *
 * 칩(`SiblingReportChips`)만 쓰던 조회를 훅으로 뺀 이유는 작성 화면이 같은 데이터를 두 곳
 * 더 쓰기 때문이다: 저장 완료 안내의 **잔여 초안**, 그리고 이어 작성 제안의 **다음 형제**.
 * 호스트가 queryKey를 손으로 다시 적으면 키는 같은데 `queryFn`·`enabled`가 갈릴 수 있고,
 * 그러면 같은 화면에서 두 목록이 서로 다른 답을 준다.
 *
 * 칩과 마찬가지로 **도메인 지식이 0이다**(§④) — 분할 그룹이 공종인지 장소인지 알지 않고,
 * 알 필요도 없다. 그래서 이 훅을 쓰는 UI 문구도 그룹 라벨을 **인용**할 뿐 이름 붙이지 않는다.
 *
 * 캐시 키가 **형제 그룹**이지 문서가 아니다 — 형제끼리는 (점검, 양식)이 같으므로 A→B로
 * 옮겨도 같은 항목을 맞고, 5분 staleTime 안에서는 재요청이 없다.
 */
import { useCallback } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { getReportInspectionId } from '@safetyhub/shared';
import { getReports, type Report } from '../../lib/reports';

/**
 * 형제 후보 조회 상한. 한 점검에서 나온 문서는 분할 그룹 수만큼이라 수십 건을 넘지 않는다
 * (구 TBM 위저드의 공종 그룹도 한 자릿수였다). 페이지네이션을 두지 않는 대신 넉넉히 한 번에 받는다.
 */
export const SIBLING_FETCH_LIMIT = 50;

export interface UseSiblingReportsResult {
  /**
   * 생성 순으로 정렬된 형제 목록(자기 자신 포함) — 분할 N건은 한 번에 태어나므로 작성자가
   * 그룹을 고른 순서와 같다. **칩에 보이는 순서가 곧 이 순서**라, "다음 형제"를 이 배열에서
   * 고르면 사용자가 화면에서 읽은 순서와 어긋나지 않는다.
   */
  siblings: Report[];
  /**
   * 첫 로드가 아직 안 끝났는가(F-7) — 빈 `siblings`가 "단건"인지 "아직 모름"인지 가른다.
   * 묶음 동사 확인 모달의 "문서 N건" 고지가 로드 전 실행되지 않게 하는 축.
   * (`isLoading` = pending ∧ fetching — enabled=false로 막힌 조회는 false라 영구 pending이 없다.)
   */
  isLoading: boolean;
  error: unknown;
  /**
   * 이 목록 전체를 버린다 — 문서 **상태를 바꾼 화면**이 성공 직후 부른다(완료·상신 등).
   *
   * 무효화 **호출**은 종전처럼 동작을 일으킨 화면 쪽에 있고(분할 생성·배포 설정·작성 저장
   * 세 선례와 같다), 여기 있는 것은 **캐시 키를 아는 책임**뿐이다. 키를 화면마다 손으로 다시
   * 적으면 이 훅이 키를 바꿀 때 조용히 어긋난다(무효화가 아무것도 못 지운다 — 실패가 보이지도
   * 않는 종류다).
   *
   * 개별 문서가 아니라 목록 전체를 버리는 이유: 상태가 바뀐 문서는 **자기 형제 목록에도**
   * 들어 있고, 앵커가 어느 문서인지는 화면이 모른다(`:id`가 앵커가 아닌 형제일 수 있다).
   */
  invalidate: () => void;
}

/**
 * @param report 기준 문서. null/undefined(아직 로드 전)면 조회하지 않는다.
 * @param companyId 목록 조회 스코프. 없으면(멤버십 로딩 중) 조회하지 않는다.
 */
/**
 * 형제 목록 캐시를 버린다 — **훅을 쓰지 않는 화면도 부를 수 있게** 모듈 함수로 둔다(2026-09-10).
 *
 * 키를 아는 책임은 이 모듈 하나다(위 `invalidate` doc). 문서 **목록**에서 삭제하는 경로는 이
 * 훅을 쓰지 않아 종전엔 `['reports']`만 다시 읽었고, 그래서 한 건을 지우고 같은 묶음의 다른
 * 문서로 들어가면 **지워진 문서가 형제 탭에 그대로 남았다**(누르면 「보고서를 찾을 수 없습니다」).
 * 상세에서만 고치면 같은 결함이 목록에 남는다 — 키를 여기 한 곳에 두고 두 화면이 함께 부른다.
 *
 * 키의 첫 조각만 준다 — 회사·점검·양식·배치가 다른 항목까지 함께 버리지만, 형제 목록은
 * 화면당 한 묶음이라 실익이 없는 정밀도다.
 */
export function invalidateSiblingReports(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: ['siblingReports'] });
}

export function useSiblingReports(
  report: Report | null | undefined,
  companyId?: string,
): UseSiblingReportsResult {
  const inspectionId = report ? getReportInspectionId(report) : null;
  const templateKey = report?.templateKey ?? null;
  // 배치 축(F-5)도 캐시 키다 — 같은 (점검, 양식)이라도 라운드가 다르면 형제 목록이 다르다.
  const splitBatchId = report?.splitBatchId ?? null;

  // 점검에 걸리지 않은 문서는 **형제 개념 자체가 없다** — `enabled`로 조회를 아예 막는다.
  //
  // 분할 산물이 아닌 문서도 마찬가지다(F-63, 2026-09-14) — 형제는 「분할 그룹」의 다른 그룹이지
  // 「같은 점검에 붙은 다른 문서」가 아니다. 종전엔 그룹 키 없이 (점검, 양식, 배치=null)만 맞으면
  // 묶어서, 분할을 쓰지 않는 양식(일일안전점검일지)에 같은 점검의 다른 일지 두 건이 「그룹」
  // 칩으로 나뉘어 보였다(2차 회귀 R36 — 사용자 「이 보고서는 분할을 지원하지 않지 않아?」).
  // 서버의 묶음 판정(`tbm-distribution-setup.service` — `!anchor.splitGroupKey`면 형제 없음)과
  // 같은 축이다. 배치 null의 grandfather 규약(아래)은 **그룹 키가 있는** 옛 분할 행에만 남는다.
  const splitGroupKey = report?.splitGroupKey ?? null;
  /**
   * 「분할 산물인가」는 캐시 키에도 들어간다(리뷰 W7, 2026-09-14). `enabled`에만 두면 react-query는
   * enabled=false여도 **같은 키의 캐시 data를 그대로 돌려준다** — 분할 문서 A를 보고 난 뒤 같은
   * (회사·점검·양식·배치)의 비분할 문서로 가면 A의 형제 목록이 그 화면에 그대로 뜬다. 키는
   * 「있고 없음」만 담는다(그룹 키 자체가 다르면 형제이고, 형제는 같은 목록을 공유해야 한다).
   */
  const hasSplitGroup = splitGroupKey != null;
  const {
    data: siblings = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['siblingReports', companyId, inspectionId, templateKey, splitBatchId, hasSplitGroup],
    enabled: Boolean(companyId && inspectionId && templateKey && hasSplitGroup),
    queryFn: async () => {
      const res = await getReports(companyId!, {
        inspectionId: inspectionId!,
        limit: SIBLING_FETCH_LIMIT,
      });
      // 같은 점검이어도 양식이 다르면 형제가 아니다(점검 하나에 여러 양식이 달릴 수 있다).
      // 생성 배치(splitBatchId)도 형제 축이다(F-5) — 같은 담당자·같은 점검을 다시 위임한
      // **다른 라운드**의 문서가 칩·잔여 초안·N건 고지에 섞이지 않게 한다. null=null은
      // 도입 전 기존 행(grandfather — 종전 판정 유지)이라 그대로 묶인다.
      // 후보 쪽도 그룹 키가 있어야 한다(W7) — 기준 문서는 분할 산물인데 같은 점검·양식·배치의
      // **비분할** 문서가 섞이면 그것이 「그룹」 칩으로 나뉜다(F-63의 반대 방향).
      const matched = res.data.filter(
        (r) =>
          r.templateKey === templateKey &&
          (r.splitBatchId ?? null) === splitBatchId &&
          r.splitGroupKey != null,
      );
      return [...matched].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    },
  });

  const queryClient = useQueryClient();
  const invalidate = useCallback(() => invalidateSiblingReports(queryClient), [queryClient]);

  return { siblings, isLoading, error, invalidate };
}
